import React from "react";

import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Stack } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceDocumentIcon, WorkspaceFolderCloseIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { MetadataTable } from "components/Deal-workspace/Workspace-metadata-components/MetadataTable";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { DealRoleGuard } from "components/guards";
import { Menu } from "components/popups";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { EntityType, FileType, RoleName } from "constants/index";
import { Routes } from "constants/routes";
import { useHighlightAfterClose, usePrompt } from "hooks";
import { useDealActions, usePickUserPermissions } from "store";
import { IFileMetaDetail, UserPermissionType } from "store/types";

import { useFolderHook } from "../Deal-workspace/Hooks/use-folder-hook";
import { useWorkspaceFilter } from "../Deal-workspace/Hooks/use-workspace-filter";
import { ColumnVisibilityMenu } from "../Deal-workspace/Menus/ColumnVisibilityMenu";
import { DocumentActions, Props as DocumentActionsProps } from "../Deal-workspace/Menus/DocumentActions";
import { FileActions, Props as FileActionsProps } from "../Deal-workspace/Menus/FileActions";
import { FolderActions, Props as FolderActionsProps } from "../Deal-workspace/Menus/FolderActions";
import { FormActions, Props as FormActionsProps } from "../Deal-workspace/Menus/FormActions";
import { BulkActionsBar } from "../Deal-workspace/Workspace-metadata-components/BulkActionsBar";
import { WorkspaceFilter } from "../Deal-workspace/Workspace-metadata-components/WorkspaceFilter";
import { ClassificationPrompt } from "../File-repository/Table/ClassificationPrompt";
import { CurrencyPrompt } from "../File-repository/Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "../File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "../File-repository/Table/InvestorPrompt";
import { StatusPrompt } from "../File-repository/Table/StatusPrompt";
import { useWorkspaceBulkActionsHook } from "./hooks/use-workspace-bulk-actions-hook";
import { useWorkspaceCellHook } from "./hooks/use-workspace-cell-hook";
import { useWorkspaceDocumentHook } from "./hooks/use-workspace-document-hook";
import { useWorkspaceFileHook } from "./hooks/use-workspace-file-hook";
import { useWorkspaceFolderHook } from "./hooks/use-workspace-folder-hook";
import { useWorkspaceFormHook } from "./hooks/use-workspace-form-hook";
import { Breadcrumbs } from "./Metadata/Breadcrubms";
import { DealFilter } from "./Metadata/DealFilter";
import { Head as MetadataHead } from "./Metadata/Head";
import { useWorkspace } from "./WorkspaceContext";

const WorkspaceFolderActions: React.FC<FolderActionsProps> = (props) => (
  <FolderActions {...props} useHook={useWorkspaceFolderHook} />
);
const WorkspaceFormActions: React.FC<FormActionsProps> = (props) => (
  <FormActions {...props} useHook={useWorkspaceFormHook} />
);
const WorkspaceDocumentActions: React.FC<DocumentActionsProps> = (props) => (
  <DocumentActions {...props} useHook={useWorkspaceDocumentHook} />
);
const WorkspaceFileActions: React.FC<FileActionsProps> = (props) => (
  <FileActions {...props} useHook={useWorkspaceFileHook} />
);

interface Props {
  recentlyAdded: boolean;
}

const GlobalFiles: React.FC<Props> = ({ recentlyAdded }) => {
  const {
    activeFilters,
    filtersUserInteracted,
    handleChangeFilter,
    currentFolder,
    globalFolderFilters,
    handleRowClick,
    columnVisibility,
    columnOrder,
    reorderColumns,
    selectedItems,
    setSelectedItems,
    selectedDeals,
    setSelectedDeals,
    deals,
    toggleColumn,
    folderMetadata,
    folderMetadataLoading: isLoading,
    refetchFolderMetadata,
    handleFetchNextPage,
    setSortBy,
    globalFolderFiltersLoading,
    dispatchDetail
  } = useWorkspace();
  const { bulkUpdateWorkspaceFilesAction } = useDealActions();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const { filtersList, dataroomOnlyKeys, isDataroom, isAggregateView, filterTypes } = useWorkspaceFilter({
    currentFolder,
    workspaceFiltersFromDB: globalFolderFilters
  });

  const title = recentlyAdded ? "Recently Added" : "All Items";
  const breadcrumbPath = recentlyAdded ? Routes.workspaceRecentlyAdded : Routes.workspaceAllFiles;

  const { onCreateNewFolder, onFileUpload, onAddForm, onAddDocument } = useFolderHook({
    folder: {
      id: currentFolder?.id || -1,
      title: currentFolder?.title || "",
      is_private: currentFolder?.is_private || false,
      parent_id: currentFolder?.parent_id || null,
      deal_id: currentFolder?.deal_id || null,
      user_id: currentFolder?.user_id || -1,
      kind: currentFolder?.kind || "folder"
    },
    refetchHandler: refetchFolderMetadata
  });

  const { handleBulkDelete, handleBulkDownload } = useWorkspaceBulkActionsHook({
    selectedItems,
    setSelectedItems,
    useItemDealId: true
  });

  const openPrompt = usePrompt();
  const {
    highlightedId: highlightedItemId,
    lastViewedIdRef: lastViewedFileIdRef,
    triggerHighlight
  } = useHighlightAfterClose();

  const handleBulkDataExtractor = async () => {
    const fileItems = selectedItems.filter(
      (item) => item.entity_type === "file" && item.type === FileType.PDF
    ) as IFileMetaDetail[];
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        source: "globalWorkspace" as const,
        initialShowParameters: false,
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  };

  const handleBulkView = async () => {
    const fileItems = selectedItems.filter(
      (item) => item.entity_type === "file" && item.type === FileType.PDF
    ) as IFileMetaDetail[];
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        initialShowParameters: false,
        source: "globalWorkspace" as const,
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  };

  const handleChangeClassification = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) return;

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: ClassificationPrompt,
      props: {
        currentClassification: null
      }
    });

    if (!result) return;

    // Bulk update all selected files with the new classification
    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      document_type: result.classification
    });

    // Update local state with server response
    updatedFiles.forEach((updatedFile) => {
      dispatchDetail({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeCurrency = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) return;

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: CurrencyPrompt,
      props: {
        currentCurrency: null
      }
    });

    if (!result || !result.currency) return;

    // Bulk update all selected files with the new currency
    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      currency: result.currency
    });

    // Update local state with server response
    updatedFiles.forEach((updatedFile) => {
      dispatchDetail({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeInvestor = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) return;

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: InvestorPrompt,
      props: {
        currentInvestorId: null,
        currentAllInvestors: false,
        currentSplitByCommitment: false,
        dealId: null,
        showGrouped: false
      }
    });

    if (!result) return;

    // Bulk update all selected files with the new investor
    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      investor_id: result.investor?.id ?? null,
      split_by_commitment: result.splitByCommitment
    });

    // Update local state with server response
    updatedFiles.forEach((updatedFile) => {
      dispatchDetail({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeEffectiveDate = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) return;

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: EffectiveDatePrompt,
      props: {
        currentDate: null
      }
    });

    if (!result) return;

    // Bulk update all selected files with the new effective date
    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      effective_date: result.effectiveDate
    });

    // Update local state with server response
    updatedFiles.forEach((updatedFile) => {
      dispatchDetail({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeStatus = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) return;

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: StatusPrompt,
      props: {
        currentStatus: "new"
      }
    });

    if (!result) return;

    // Bulk update all selected files with the new status
    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      file_status: result.status
    });

    // Update local state with server response
    updatedFiles.forEach((updatedFile) => {
      dispatchDetail({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const details = folderMetadata?.details ?? [];

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex"
      }}
      gap="16px"
    >
      <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          links={[
            { path: breadcrumbPath, title: "Workspace" },
            { path: breadcrumbPath, title }
          ]}
        />
        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <MetadataHead title={title} icon="folder" />
          <ColumnVisibilityMenu
            columnVisibility={columnVisibility}
            onToggle={toggleColumn}
            kind="folder"
            showDealColumn
            showDataroomColumns
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap="10px">
            <DealFilter deals={deals} selectedDeals={selectedDeals} setSelectedDeals={setSelectedDeals} />

            <WorkspaceFilter
              isLoading={globalFolderFiltersLoading}
              handleChangeFilter={handleChangeFilter}
              filtersList={filtersList}
              filterTypes={filterTypes}
              applyMode
              state={activeFilters}
              isDataroom={isDataroom}
              isAggregateView={isAggregateView}
              dataroomOnlyKeys={dataroomOnlyKeys}
              defaultFilterKeys={["document_type", "file_type", "investor"]}
              skipDefaults={filtersUserInteracted}
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap="10px">
            <BulkActionsBar
              selectedItems={selectedItems}
              onClose={() => setSelectedItems([])}
              onView={handleBulkView}
              onExtract={handleBulkDataExtractor}
              onDownload={handleBulkDownload}
              onChangeClassification={handleChangeClassification}
              onChangeCurrency={handleChangeCurrency}
              onChangeInvestor={handleChangeInvestor}
              onChangeEffectiveDate={handleChangeEffectiveDate}
              onChangeStatus={handleChangeStatus}
              onDelete={handleBulkDelete}
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack
        sx={{
          overflow: "hidden",
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {details.length || isLoading || !!Object.keys(activeFilters).length || !!selectedDeals.length ? (
          <MetadataTable
            data={details}
            isLoading={isLoading}
            kind="folder"
            onRowClick={handleRowClick}
            onFetchNextPage={handleFetchNextPage}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            reorderColumns={reorderColumns}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            refetchFolderMetadata={refetchFolderMetadata}
            useUpdateCellHookOverride={useWorkspaceCellHook}
            FolderActionsComponent={WorkspaceFolderActions}
            FormActionsComponent={WorkspaceFormActions}
            DocumentActionsComponent={WorkspaceDocumentActions}
            FileActionsComponent={WorkspaceFileActions}
            showDealColumn
            showDataroomColumns
            manualSorting
            onSortingChange={setSortBy}
            highlightedItemId={highlightedItemId}
          />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceGlobalFiles.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceGlobalFiles.description,
              externalText: EMPTY_CARD_CONTENT.workspaceGlobalFiles.externalDescription
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <DealRoleGuard
                availableRoles={[RoleName.Admin, RoleName.Analyst]}
                hideChild={!!noAccessPermission || !!isViewerPermission}
              >
                <Menu
                  icon={
                    <Button
                      sx={{ textTransform: "none", bgcolor: "#071F35" }}
                      startIcon={<AddIcon />}
                      variant="contained"
                    >
                      Add item
                    </Button>
                  }
                  keepTriggerEnabled
                  iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  options={[
                    {
                      title: "New folder",
                      icon: <WorkspaceFolderCloseIcon sx={{ fill: "#5d7782" }} />,
                      onClick: () => onCreateNewFolder?.(),
                      disabled: !onCreateNewFolder
                    },
                    {
                      title: "New document",
                      icon: <WorkspaceDocumentIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddDocument?.(),
                      disabled: !onAddDocument
                    },
                    {
                      title: "New form",
                      icon: <WorkspaceFormIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddForm?.(),
                      disabled: !onAddForm
                    },
                    {
                      title: "Upload file",
                      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onFileUpload?.(),
                      disabled: !onFileUpload
                    }
                  ]}
                />
              </DealRoleGuard>
            }
          />
        )}
      </Stack>
    </Stack>
  );
};

export const AllFiles: React.FC = () => <GlobalFiles recentlyAdded={false} />;
export const RecentlyAdded: React.FC = () => <GlobalFiles recentlyAdded={true} />;
